import { Aide } from '@/components/ui/aide';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BarChart3,
  Banknote,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Download,
  Droplets,
  FileText,
  MoreVertical,
} from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { messageErreur } from '../lib/erreurs';
import { texteSur, titreSectionPilotage } from '../lib/libelles';
import { downloadBlob } from '../lib/download';
import {
  cn,
  formatCompactCurrency,
  formatCompactVolume,
  formatNumber,
} from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import BusinessActionContract from '../components/business/BusinessActionContract';
import { SectionCard } from '../components/dashboard';

/* ------------------------------------------------------------------ */
/* Les quatre familles de rapports                                      */
/* ------------------------------------------------------------------ */

/**
 * Le cockpit métier renvoie ses sections sous quatre clés stables
 * (`group`, `transport`, `stations`, `control_fuel`). Chacune est une
 * **famille** de rapport, pas un état : « Transport » n'est ni bon ni
 * mauvais, il est. C'est exactement le travail de l'échelle catégorielle
 * `--chart-1..5` décrite dans `index.css` — identité, pas statut. Les tons
 * sémantiques (success / destructive / flame) restent réservés au verdict.
 *
 * Une famille = une teinte, et cette teinte est la même dans la pastille de
 * la rangée compacte et dans la carte de la section : si la couleur changeait
 * entre les deux endroits, elle cesserait de désigner la famille et ne
 * désignerait plus que l'emplacement de la tuile.
 *
 * `ordre` fige la grille : selon le pack souscrit, l'API renvoie deux, trois
 * ou quatre sections. Sans ordre imposé, la même famille changerait de
 * colonne d'un client à l'autre — et la position deviendrait un signal faux.
 */
const FAMILLES = {
  group: {
    nom: 'Carburant',
    ordre: 1,
    teinte: 'bg-chart-3/15 text-chart-3',
    point: 'bg-chart-3',
    route: '/stock',
    // Trois mesures nommées court pour tenir dans une case, mais reliées à
    // l'étiquette exacte du backend : on lit une vraie valeur ou rien.
    mesures: [
      { court: 'Stock', cle: 'stock', repli: 'Stock consolidé' },
      {
        court: 'Alertes',
        cle: 'open_alerts',
        repli: 'Alertes ouvertes',
        aide: 'Alertes automatiques en attente de décision (même chiffre que « à traiter » sur l’écran Alertes)',
      },
      {
        court: 'Anomalies',
        cle: 'risk_families',
        repli: 'Missions à risque',
        aide: 'Familles de travaux à arbitrer',
      },
    ],
  },
  transport: {
    nom: 'Transport',
    ordre: 2,
    teinte: 'bg-chart-1/15 text-chart-1',
    point: 'bg-chart-1',
    route: '/trips',
    mesures: [
      { court: 'À risque', cle: 'losses_at_risk', repli: 'Pertes à risque' },
      { court: 'Litres', cle: 'suspect_liters', repli: 'Litres suspects' },
      { court: 'Bloqué', cle: 'billing_blocked', repli: 'Cash bloqué' },
    ],
  },
  stations: {
    nom: 'Réseau',
    ordre: 3,
    teinte: 'bg-chart-2/15 text-chart-2',
    point: 'bg-chart-2',
    route: '/stations',
    mesures: [
      { court: 'Stations', cle: 'active_stations', repli: 'Stations actives' },
      { court: 'À risque', cle: 'tanks_at_risk', repli: 'Cuves à risque' },
      { court: 'Ventes', cle: 'sales_value', repli: 'Ventes' },
    ],
  },
  control_fuel: {
    nom: 'Contrôle',
    ordre: 4,
    teinte: 'bg-chart-4/15 text-chart-4',
    point: 'bg-chart-4',
    route: '/control-fuel',
    mesures: [
      { court: 'Sites', cle: 'sites', repli: 'Sites' },
      { court: 'Litres', cle: 'issued_liters', repli: 'Litres sortis' },
      { court: 'Dépenses', cle: 'internal_spend', repli: 'Dépense interne' },
    ],
  },
};

/**
 * Une cinquième famille peut apparaître si le backend ajoute un cockpit : elle
 * prend la dernière teinte de l'échelle et garde ses propres étiquettes. Pas
 * de route de sortie — inventer une destination serait pire que ne pas en
 * proposer.
 */
const FAMILLE_INCONNUE = {
  ordre: 9,
  teinte: 'bg-chart-5/15 text-chart-5',
  point: 'bg-chart-5',
  route: undefined,
  mesures: null,
};

export const familleDe = (section) => {
  const connue = FAMILLES[section?.key];
  if (connue) return connue;
  return { ...FAMILLE_INCONNUE, nom: section?.title || 'Autre' };
};

/* ------------------------------------------------------------------ */
/* Lecture des valeurs                                                  */
/* ------------------------------------------------------------------ */

/**
 * « — » et « 0 » ne disent pas la même chose. « 0 litre suspect » est un
 * résultat de contrôle ; « — » avoue que la mesure n'a pas été calculée parce
 * que le module correspondant n'est pas activé. Afficher 0 dans le second cas
 * revient à certifier une absence d'anomalie qu'on n'a jamais vérifiée.
 */
const ABSENT = '—';

const formatMesure = (metric) => {
  if (!metric || metric.value === null || metric.value === undefined) return ABSENT;
  if (metric.format === 'money') return formatCompactCurrency(metric.value);
  if (metric.format === 'liters') return formatCompactVolume(metric.value);
  if (typeof metric.value === 'number') return formatNumber(metric.value);
  return String(metric.value);
};

/**
 * Cherche une mesure par sa clé stable (`metric.key`). Repli sur l'ancien
 * libellé uniquement pour une réponse sans clé ; absente, elle vaut « — ».
 */
const mesureParCle = (section, cle, repli) => {
  const metrics = section?.metrics || [];
  return (
    metrics.find((metric) => metric?.key === cle) ||
    (repli ? metrics.find((metric) => !metric?.key && metric?.label === repli) : undefined)
  );
};

/** Les trois cases d'une carte de famille, dans l'ordre de la maquette. */
export const mesuresDe = (section, famille) => {
  if (famille.mesures) {
    return famille.mesures.map(({ court, cle, repli, aide }) => {
      const metric = mesureParCle(section, cle, repli);
      const libelleServeur = typeof metric?.label === 'string' ? metric.label.trim() : '';
      return {
        label: court,
        valeur: formatMesure(metric),
        aide,
        // Le libellé du serveur, quand il précise ce que la case mesure
        // (« Carburant chargé, non livré » pour le stock d'un transporteur).
        precision:
          libelleServeur && libelleServeur !== court && libelleServeur !== repli
            ? libelleServeur
            : null,
      };
    });
  }
  // Famille inconnue : on montre ses trois premières mesures telles quelles.
  return (section?.metrics || []).slice(0, 3).map((metric) => ({
    label: metric.label,
    valeur: formatMesure(metric),
  }));
};

/* ------------------------------------------------------------------ */
/* Dates                                                                */
/* ------------------------------------------------------------------ */

/** « Lundi 14 avril 2025 ». Le jour de la semaine aide à situer un rapport. */
const dateLongue = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date inconnue';
  const texte = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return texte.charAt(0).toUpperCase() + texte.slice(1);
};

const TOUTES_LES_DATES = '__toutes__';

/** Les rôles que le serveur autorise à lire les rapports de la direction. */
const ROLES_AUTORISES = ['SUPER_ADMIN', 'ADMIN_SOCIETE', 'LOGISTIQUE'];

/**
 * Le bilan mensuel de la flotte est rangé dans la même table que les rapports
 * du jour (type `monthly_bilan`, date « AAAA-MM »): il a sa propre page
 * (Rapports) et ne doit pas apparaître ici comme un rapport quotidien.
 */
const estRapportQuotidien = (report) =>
  report?.report_type !== 'monthly_bilan' && !/^\d{4}-\d{2}$/.test(String(report?.report_date || ''));

/* ------------------------------------------------------------------ */
/* Cartes de chiffres clés (colonne de droite des rapports métiers)     */
/* ------------------------------------------------------------------ */

const TONS_CHIFFRE = {
  positif: 'bg-success/10 text-success',
  critique: 'bg-destructive/10 text-destructive',
  neutre: 'bg-primary/10 text-primary',
};

/**
 * Une carte = un chiffre, sa légende, une phrase d'explication et une sortie.
 * Elle est cliquable en entier : lien vers l'écran où l'on agit, ou bouton si
 * l'action reste sur la page (descendre au contrôle avant génération).
 */
const CarteChiffre = ({ icone: Icone, valeur, label, aide, ton, to, onClick, infobulle }) => {
  const classes = cn(
    'group flex w-full min-w-0 items-center gap-3 rounded-xl border border-border/70 bg-card p-3 text-left shadow-sm transition-colors',
    'hover:border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  );
  const contenu = (
    <>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          TONS_CHIFFRE[ton] || TONS_CHIFFRE.neutre,
        )}
      >
        <Icone className="h-[18px] w-[18px]" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-lg font-bold leading-tight tabular-nums text-foreground">{valeur}</span>
          <span className="text-sm font-semibold text-foreground">{label}</span>
        </span>
        <span className="block text-xs leading-snug text-muted-foreground">{aide}</span>
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid="carte-chiffre" className={classes} title={infobulle || undefined}>
        {contenu}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid="carte-chiffre" className={classes} title={infobulle || undefined}>
      {contenu}
    </button>
  );
};

/* ------------------------------------------------------------------ */

export default function DailyReportsPage() {
  const navigate = useNavigate();
  const { user, managesDepot } = useAuth();
  const autorise = !user?.role || ROLES_AUTORISES.includes(user.role);
  const [executive, setExecutive] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [reports, setReports] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  // Période : le select d'en-tête filtre les rapports archivés sur leur
  // `report_date`. Les options ne sont pas un calendrier libre — ce sont les
  // dates qui existent réellement dans les données chargées.
  const [periode, setPeriode] = useState(TOUTES_LES_DATES);
  const [toutAfficher, setToutAfficher] = useState(false);

  // La pastille « N à voir » de la bannière doit mener quelque part : elle
  // descend vers les familles, seul endroit où les points d'attention sont
  // décomposés. Un compteur qui ne se clique pas laisse chercher.
  const famillesRef = useRef(null);
  const controleRef = useRef(null);

  const load = async () => {
    const [executiveRes, readinessRes, reportsRes] = await Promise.all([
      api.get('/daily-reports/executive'),
      api.get('/daily-reports/readiness'),
      api.get('/daily-reports'),
    ]);
    setExecutive(executiveRes.data || null);
    setReadiness(readinessRes.data || null);
    setReports((reportsRes.data || []).filter(estRapportQuotidien));
  };

  useEffect(() => {
    // Un rôle de station n'a pas accès à ces rapports: inutile d'appeler le serveur.
    if (!autorise) return;
    load().catch((error) => toast.error(messageErreur(error, 'Impossible de charger les rapports de la direction.')));
  }, [autorise]);

  const generate = async () => {
    setIsGenerating(true);
    try {
      await api.post('/daily-reports/generate-executive');
      await load();
      toast.success('Rapport de la direction généré');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de générer le rapport de la direction.'));
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await api.get('/daily-reports/executive.pdf', { responseType: 'blob' });
      downloadBlob(response, 'rapport-direction-carburant.pdf', 'application/pdf');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de télécharger le rapport en PDF.'));
    }
  };

  const totals = executive?.totals || {};
  const priorityActions = executive?.priority_actions || [];

  /** Sections triées par famille, pour que la grille ne bouge pas. */
  const sections = useMemo(() => {
    const brutes = executive?.sections || [];
    return [...brutes].sort((a, b) => familleDe(a).ordre - familleDe(b).ordre);
  }, [executive]);

  /** Les dates réellement présentes dans les rapports archivés, récentes d'abord. */
  const datesDisponibles = useMemo(() => {
    const uniques = new Set(
      reports.map((report) => report.report_date).filter(Boolean),
    );
    return [...uniques].sort().reverse();
  }, [reports]);

  const rapportsFiltres = useMemo(() => {
    if (periode === TOUTES_LES_DATES) return reports;
    return reports.filter((report) => report.report_date === periode);
  }, [reports, periode]);

  const rapportsVisibles = toutAfficher ? rapportsFiltres : rapportsFiltres.slice(0, 6);

  // Les points d'attention sont les actions prioritaires renvoyées par le
  // cockpit : c'est la seule liste que la page charge et qui réclame un
  // arbitrage. On ne l'additionne pas au compte de risques, qui les recouvre
  // en partie — un total gonflé est aussi faux qu'un total absent.
  const pointsAttention = priorityActions.length;

  // Détail du total « en jeu » (serveur: totals.stake_breakdown), une ligne
  // par poste, en infobulle de la carte correspondante.
  const detailEnJeu = (format) => {
    const lignes = (Array.isArray(totals.stake_breakdown) ? totals.stake_breakdown : [])
      .filter((ligne) => ligne && ligne.format === format && ligne.value !== null && ligne.value !== undefined)
      .map(
        (ligne) =>
          `${ligne.label} : ${
            format === 'money' ? formatCompactCurrency(ligne.value) : formatCompactVolume(ligne.value)
          }`,
      );
    return lignes.length ? lignes.join('\n') : undefined;
  };

  const aDesRapports = reports.length > 0;

  const versFamilles = () =>
    famillesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Descend au contrôle « Avant de générer le rapport » (ou, à défaut, aux
  // familles) et y pose le focus pour le clavier.
  const versControle = () => {
    const cible = controleRef.current || famillesRef.current;
    cible?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    cible?.focus?.({ preventScroll: true });
  };

  /* --- Les quatre chiffres clés, à droite des rapports métiers --------- */
  const cartesChiffres = [
    {
      cle: 'pret',
      icone: CheckCircle2,
      // `readiness` absent = pas encore chargé : « 0 % » ferait croire à un
      // blocage alors qu'on n'a rien mesuré.
      valeur: readiness ? `${readiness.score ?? 0} %` : ABSENT,
      label: 'Prêt à générer',
      aide: readiness?.ready
        ? 'Les données suffisent : le rapport peut être généré.'
        : 'Voir ce qui manque avant de générer le rapport.',
      ton: readiness?.ready ? 'positif' : 'critique',
      onClick: versControle,
    },
    {
      cle: 'argent',
      icone: Banknote,
      valeur:
        totals.money_at_stake === undefined
          ? ABSENT
          : formatCompactCurrency(totals.money_at_stake),
      label: 'Argent en jeu',
      aide: 'Montants à encaisser ou à risque : ouvrir les factures.',
      ton: 'neutre',
      to: '/invoices',
      infobulle: detailEnJeu('money'),
    },
    {
      cle: 'carburant',
      icone: Droplets,
      valeur:
        totals.liters_at_stake === undefined
          ? ABSENT
          : formatCompactVolume(totals.liters_at_stake),
      label: 'Carburant en jeu',
      aide: managesDepot === false
        ? 'Carburant chargé et pas encore livré : ouvrir le suivi.'
        : 'Carburant concerné par les écarts : ouvrir le stock.',
      ton: 'neutre',
      to: '/stock',
      infobulle: detailEnJeu('liters'),
    },
    {
      cle: 'risque',
      icone: AlertTriangle,
      valeur:
        totals.risk_count === undefined ? ABSENT : formatNumber(totals.risk_count),
      label: 'Points à risque',
      aide: 'Alertes ouvertes à traiter : ouvrir la liste.',
      // Rouge seulement s'il y a réellement un risque.
      ton: totals.risk_count ? 'critique' : 'neutre',
      to: '/alerts',
    },
  ];

  if (!autorise) {
    return (
      <div className="mx-auto w-full max-w-page px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <EmptyState
          illustration="donnee"
          title="Page réservée à la direction"
          description="Cette page est réservée à l’administrateur et à la logistique. Pour vos ventes et votre caisse, utilisez les écrans de votre station."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Rapports"
        title="Vue d’ensemble"
        description="Vos chiffres clés du jour en un coup d’œil : carburant, transport, stations."
        alertesSousTitre
        alertes={[
          {
            // La mention « Rapport prêt » serait un mensonge devant une page
            // sans rapport archivé: l'état suit les données réelles.
            niveau: aDesRapports ? 'info' : 'attention',
            court: aDesRapports ? 'Rapport prêt' : 'Aucun rapport',
            texte: aDesRapports
              ? 'Votre rapport est disponible pour consultation.'
              : readiness?.ready
                ? 'Aucun rapport n’a encore été généré. Les données du jour sont suffisantes : lancez la génération.'
                : texteSur(
                    readiness?.blocking_reasons?.[0]?.message ||
                      readiness?.business_context?.next_action,
                    'Aucun rapport n’a encore été généré.',
                  ),
            action:
              !aDesRapports && readiness?.ready
                ? { label: 'Générer le rapport', onClick: generate }
                : undefined,
          },
          // Seulement s'il y a réellement quelque chose à regarder: un « 0 à
          // voir » permanent apprend à ignorer l'endroit où l'alerte finira par
          // s'afficher.
          pointsAttention > 0 && {
            niveau: 'attention',
            court: 'à voir',
            infobulle: 'Familles de travaux à arbitrer',
            nombre: pointsAttention,
            texte: `${pointsAttention} point(s) demandent votre décision dans les rapports métiers.`,
            action: { label: 'Voir les rapports métiers', onClick: versFamilles },
          },
        ]}
        actions={
          <>
            {/* `<select>` natif : `index.css` le thème déjà (fond `--card`,
                texte `--foreground`, options comprises), donc il suit le mode
                sombre sans qu'on ait à le réhabiller ici. */}
            <label className="relative inline-flex items-center">
              <Calendar
                className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <span className="sr-only">Période des rapports</span>
              <select
                value={periode}
                onChange={(event) => {
                  setPeriode(event.target.value);
                  setToutAfficher(false);
                }}
                disabled={datesDisponibles.length === 0}
                className="h-8 truncate rounded-md border border-input bg-card pl-9 pr-3 text-xs font-medium text-foreground max-sm:w-9 max-sm:appearance-none max-sm:pr-0 max-sm:text-transparent max-sm:[&>option]:text-foreground"
              >
                <option value={TOUTES_LES_DATES}>
                  {datesDisponibles.length === 0
                    ? 'Aucune date'
                    : 'Toutes les dates'}
                </option>
                {datesDisponibles.map((date) => (
                  <option key={date} value={date}>
                    {dateLongue(date)}
                  </option>
                ))}
              </select>
            </label>

            <Button variant="outline" onClick={downloadPdf} title="Télécharger le rapport en PDF">
              <Download aria-hidden="true" />
              PDF
            </Button>

            <Button onClick={generate} disabled={isGenerating || readiness?.ready === false}>
              <BarChart3 aria-hidden="true" />
              {isGenerating ? 'Génération en cours…' : 'Générer le rapport'}
            </Button>
          </>
        }
      />

      {readiness?.action_contract && (
        <div
          ref={controleRef}
          tabIndex={-1}
          className="scroll-mt-4 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BusinessActionContract
            title="Avant de générer le rapport"
            contract={readiness.action_contract}
            blockers={(readiness.blocking_reasons || []).map((item) => item.message)}
            compact
          />
        </div>
      )}

      <SectionCard
        ref={famillesRef}
        icon={BarChart3}
        title={
          <span className="inline-flex items-center gap-1">
            Rapports métiers
            <Aide titre="Rapports métiers">Une carte par famille : ce qu'elle mesure et où aller le vérifier.</Aide>
          </span>
        }
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_auto_minmax(17rem,21rem)] lg:gap-6">
        <div className="min-w-0">
        {sections.length === 0 ? (
          <EmptyState
            illustration="donnee"
            title="Aucun rapport à lire pour l’instant"
            description="Cette page résume la journée pour la direction : litres livrés, argent encaissé, ce qui a bloqué et la décision à prendre. Elle se remplit à partir des trajets, commandes, ventes station et sorties de carburant enregistrés."
            actionLabel={readiness?.ready ? 'Générer le rapport' : 'Voir les trajets du jour'}
            // Tant que la journée n'a rien à raconter, générer produirait un
            // rapport vide : on renvoie vers ce qui l'alimente.
            onAction={readiness?.ready ? generate : () => navigate('/trips')}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
            {sections.map((section) => {
              const famille = familleDe(section);
              const mesures = mesuresDe(section, famille);

              return (
                <article
                  key={section.key}
                  className="flex flex-col rounded-xl border border-border/70 bg-card p-4 shadow-sm"
                >
                  <header className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                          famille.teinte,
                        )}
                      >
                        <FileText className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="truncate text-sm font-bold text-foreground">
                          {famille.nom}
                        </h3>
                        {/* Le titre complet du cockpit reste accessible en
                            infobulle : la carte est trop étroite pour lui. */}
                        <p className="truncate text-xs text-muted-foreground" title={titreSectionPilotage(section)}>
                          {titreSectionPilotage(section)}
                        </p>
                      </div>
                    </div>
                    <ChevronRight
                      className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50"
                      aria-hidden="true"
                    />
                  </header>

                  {/* Empilées, et non en trois colonnes: dans une carte de
                      260 px, trois colonnes laissent 80 px par mesure et
                      « Anomalies » devenait « ANOM… », « 503 247 L » devenait
                      « 5032… ». Une mesure tronquée ne se devine pas, elle se
                      perd. En ligne, l'étiquette prend la place qu'il lui faut
                      et la valeur se lit à droite, alignée sur les voisines. */}
                  <dl className="mt-4 space-y-1.5">
                    {mesures.map((mesure) => (
                      <div
                        key={`${section.key}-${mesure.label}`}
                        className="flex items-baseline justify-between gap-3 rounded-lg bg-muted/60 px-2.5 py-1.5"
                      >
                        <dt
                          className={cn(
                            'min-w-0 text-xs font-medium text-muted-foreground',
                            mesure.aide && 'cursor-help',
                          )}
                          title={mesure.aide || undefined}
                        >
                          {mesure.label}
                          {mesure.precision && (
                            <span className="block truncate text-[11px] font-normal">
                              {mesure.precision}
                            </span>
                          )}
                        </dt>
                        <dd className="shrink-0 text-sm font-bold tabular-nums text-foreground">
                          {mesure.valeur}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  {/* `mt-auto` : les quatre cartes n'ont pas le même nombre de
                      lignes de titre, le bouton doit quand même s'aligner. */}
                  <div className="mt-auto pt-4">
                    {famille.route ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(famille.route)}
                      >
                        Ouvrir
                      </Button>
                    ) : (
                      // Famille non cartographiée : aucune destination connue.
                      // Un bouton qui n'ouvre rien coûte plus qu'il ne rapporte.
                      <p className="text-center text-xs text-muted-foreground">
                        {ABSENT}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          className="h-px w-full bg-border lg:h-auto lg:w-px"
        />

        <div
          aria-label="Chiffres clés du jour"
          className="grid min-w-0 grid-cols-1 content-start gap-3 sm:grid-cols-2 lg:grid-cols-1"
        >
          {cartesChiffres.map(({ cle, ...carte }) => (
            <CarteChiffre key={cle} {...carte} />
          ))}
        </div>
        </div>
      </SectionCard>

      <SectionCard
        icon={FileText}
        title={
          <span className="inline-flex items-center gap-1">
            Derniers rapports
            {periode === TOUTES_LES_DATES && (
              <Aide titre="Derniers rapports">Chaque rapport généré est archivé, daté, pour être relu ou renvoyé.</Aide>
            )}
          </span>
        }
        subtitle={periode === TOUTES_LES_DATES ? undefined : dateLongue(periode)}
        actionLabel={
          rapportsFiltres.length > 6
            ? toutAfficher
              ? 'Réduire'
              : 'Voir tout'
            : undefined
        }
        onAction={
          rapportsFiltres.length > 6 ? () => setToutAfficher((etat) => !etat) : undefined
        }
        contentClassName="space-y-2"
      >
        {rapportsFiltres.length === 0 ? (
          <EmptyState
            // Une liste vide parce qu'on a filtré sur une date n'est pas une
            // liste vide parce qu'il n'existe rien : la sortie diffère.
            variant={periode === TOUTES_LES_DATES ? 'empty' : 'filtered'}
            illustration={periode === TOUTES_LES_DATES ? 'donnee' : undefined}
            title={
              periode === TOUTES_LES_DATES
                ? 'Aucun rapport archivé'
                : 'Aucun rapport à cette date'
            }
            description={
              periode === TOUTES_LES_DATES
                ? 'Les rapports générés s’empilent ici, du plus récent au plus ancien, avec leur date et leur PDF téléchargeable.'
                : 'Aucun rapport n’a été généré ce jour-là. Revenez à toutes les dates pour voir l’historique complet.'
            }
            actionLabel={periode === TOUTES_LES_DATES ? undefined : 'Toutes les dates'}
            onAction={
              periode === TOUTES_LES_DATES
                ? undefined
                : () => setPeriode(TOUTES_LES_DATES)
            }
          />
        ) : (
          rapportsVisibles.map((report) => (
            <div
              key={report.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-border/70 bg-card px-3 py-2.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <FileText className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {texteSur(report.title, 'Rapport du jour')}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {report.report_date ? dateLongue(report.report_date) : ABSENT}
                </p>
              </div>

              {/* Le badge lit `status` plutôt que de supposer : une ligne
                  archivée n'est pas forcément une ligne exploitable. */}
              <Badge variant={report.status === 'generated' ? 'success' : 'muted'}>
                {report.status === 'generated' ? 'Disponible' : 'En préparation'}
              </Badge>

              <Button variant="outline" size="sm" onClick={downloadPdf}>
                <Download aria-hidden="true" />
                Télécharger
              </Button>

              {/* Le menu ne propose que des actions qui existent déjà sur cette
                  page. Un « ⋮ » rempli d'entrées inertes est un piège. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={`Actions pour ${texteSur(report.title, 'ce rapport')}`}>
                    <MoreVertical aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={downloadPdf}>
                    Télécharger le PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={generate}
                    disabled={isGenerating || readiness?.ready === false}
                  >
                    Régénérer le rapport
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))
        )}
      </SectionCard>
    </div>
  );
}
